import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Globe, Activity, ChevronLeft, LogOut, Menu, X, Plus, LayoutDashboard, Zap, BookOpen, Users, ShieldCheck, Mail, MessageSquare, FlaskConical, Cpu, Webhook, Flame, Target, Server, Volume2, Palette, Bot, FileJson,
} from 'lucide-react';
import { ThemeToggle } from '../../components/ThemeToggle';
import { BroadcastBanner } from '../../components/BroadcastBanner';
import { BudgetAlertBanner } from '../../components/custom/BudgetAlertBanner';
import { BrandWordmark } from '../../components/BrandWordmark';

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const nav = [
    { name: 'Overview', href: '/admin', icon: LayoutDashboard, exact: true },
    { name: 'AI Assistant', href: '/admin/ai-assistant', icon: Bot },
    { name: 'Domains', href: '/admin/domains', icon: Globe },
    { name: 'Branding', href: '/admin/branding', icon: Palette },
    { name: 'Call Master', href: '/admin/call-master', icon: Activity },
    { name: 'Knowledge Bases', href: '/admin/knowledge-bases', icon: BookOpen },
    { name: 'Site KB Configs', href: '/admin/kb-site-configs', icon: Globe },
    { name: 'Site-Scope Editor', href: '/admin/site-scope', icon: Users },
    { name: 'Scope Leak Alarm', href: '/admin/scope-leaks', icon: ShieldCheck },
    { name: 'Email Safety', href: '/admin/email', icon: Mail },
    { name: 'VoiceFabric', href: '/admin/voicefabric', icon: Zap },
    { name: 'User Activity', href: '/admin/activity', icon: Users },
    { name: 'Audit Intakes', href: '/admin/audit-intakes', icon: Mail },
    { name: 'Phrase Overrides', href: '/admin/phrase-overrides', icon: MessageSquare },
    { name: 'TFVF V1.2 Calibration', href: '/admin/tfvf-v1-2-calibration', icon: FlaskConical },
    { name: 'YVF Calibration', href: '/admin/yvf', icon: FlaskConical },
    { name: 'Archetype Playbooks', href: '/admin/archetype-playbooks', icon: BookOpen },
    { name: 'Compliance Scorecard', href: '/admin/compliance-scorecard', icon: Target },
    { name: 'Wasted Warm Leads', href: '/admin/wasted-warm-leads', icon: Flame },
    { name: 'Yabbit Webhook', href: '/admin/yabbit-webhook', icon: Webhook },
    { name: 'GymSales API', href: '/admin/gymsales', icon: Server },
    { name: 'Yabbit SMS', href: '/admin/yabbit-sms', icon: Server },
    { name: 'Onboarding voice', href: '/admin/onboarding', icon: Volume2 },
    { name: 'LLM Provider', href: '/admin/llm-provider', icon: Cpu },
    { name: 'LLM Routing', href: '/admin/llm-routing', icon: Cpu },
    { name: 'Queue Watchdog', href: '/admin/queue-watchdog', icon: Activity },
    { name: 'vCon Export', href: '/admin/vcon', icon: FileJson },
    { name: 'Demo Leads', href: '/admin/demo-leads', icon: Users },
    { name: 'AI Cost Statements', href: '/admin/cost-statements', icon: Cpu },
    { name: 'Rebaseline Review', href: '/admin/rebaseline-review', icon: FlaskConical },
    { name: 'System Migrations', href: '/admin/system-migrations', icon: ShieldCheck },
  ];

  const isActive = (item) => item.exact ? location.pathname === item.href : location.pathname.startsWith(item.href);

  return (
    <div className="min-h-screen bg-[#0D1117] flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0F1C] border-r border-white/5 flex flex-col transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-white/5 shrink-0">
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_call-quality-grade/artifacts/ehb854bc_1.Landscape.png"
              alt="Yabbit VoiceFabric"
              className="h-8 w-auto object-contain bg-white rounded-lg px-2 py-1"
            />
            <BrandWordmark variant="compact" className="mt-2 block" testId="admin-sidebar-brand" />
          </div>
          <button className="lg:hidden text-white/40 hover:text-white" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav — scrolls when longer than viewport so User + Logout stay reachable */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-5 space-y-0.5 admin-sidebar-scroll">
          {nav.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`admin-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  active ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/80 hover:bg-white/5'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="w-4 h-4 mr-3" />
                {item.name}
              </Link>
            );
          })}

          <div className="pt-4 pb-2 px-3">
            <span className="text-[10px] font-medium text-white/20 uppercase tracking-widest">Actions</span>
          </div>
          <Link
            to="/admin/domains/new"
            className="flex items-center px-3 py-2.5 rounded-lg text-sm font-medium text-teal-400 hover:bg-teal-400/10 transition-all"
            data-testid="admin-nav-add-domain"
          >
            <Plus className="w-4 h-4 mr-3" />
            Add Domain
          </Link>
        </nav>

        {/* Back + User */}
        <div className="px-3 py-4 border-t border-white/5 space-y-2 shrink-0">
          <ThemeToggle variant="sidebar" />
          <button
            onClick={() => navigate('/select-mode')}
            className="flex items-center w-full px-3 py-2 text-sm text-white/30 hover:text-white/70 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>
          <div className="flex items-center px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-xs font-bold mr-3">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white/70 font-medium truncate">{user?.name}</p>
              <p className="text-[10px] text-white/30 truncate">{user?.email}</p>
            </div>
            <button onClick={() => { logout(); navigate('/login'); }} className="text-white/20 hover:text-white/60 ml-2">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 flex items-center px-4 py-3 bg-[#0D1117] border-b border-white/5 lg:hidden">
          <button onClick={() => setSidebarOpen(true)} className="text-white/50 hover:text-white mr-4">
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-white/70 text-sm font-medium">Admin Console</span>
          <div className="ml-auto">
            <ThemeToggle variant="icon" />
          </div>
        </header>
        <BudgetAlertBanner />
        <BroadcastBanner />
        <main className="flex-1 p-6 md:p-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
